/* Глобальный контекст: профиль, справочники, счётчики алертов.
   Изменения из бота видны в вебе — данные перечитываются при возврате фокуса (ТЗ 11). */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, ensureAuth, logout } from "./api.js";
import { cacheKey, invalidate, invalidateAll, peek, put, shared } from "./cache.js";

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);

const DEV_USER_KEY = "mera.devUser";

export function AppProvider({ children }) {
  const [me, setMe] = useState(null);
  const [meta, setMeta] = useState(null);
  const [alerts, setAlerts] = useState({ critical: [], soft: [], counts: { critical: 0, soft: 0 } });
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const mounted = useRef(true);

  const loadAlerts = useCallback(async () => {
    try {
      const a = await api.get("/alerts");
      if (mounted.current) setAlerts(a);
    } catch {
      /* сеть могла моргнуть — счётчики не критичны */
    }
  }, []);

  const boot = useCallback(async () => {
    try {
      setError(null);
      const devUser = localStorage.getItem(DEV_USER_KEY) || "Богдан";
      const user = await ensureAuth(devUser);
      const m = await api.get("/meta");
      if (!mounted.current) return;
      setMe(user);
      setMeta(m);
      setReady(true);
      loadAlerts();
    } catch (e) {
      if (mounted.current) {
        setError(e.message || "Не удалось подключиться к серверу");
        setReady(true);
      }
    }
  }, [loadAlerts]);

  useEffect(() => {
    mounted.current = true;
    boot();
    return () => {
      mounted.current = false;
    };
  }, [boot]);

  // Данные могли измениться через бота, пока приложение было свёрнуто
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState !== "visible") return;
      // Пока приложение было свёрнуто, данные могли уехать — кэш больше не свежий
      invalidateAll();
      loadAlerts();
    };
    document.addEventListener("visibilitychange", onFocus);
    return () => document.removeEventListener("visibilitychange", onFocus);
  }, [loadAlerts]);

  const switchDevUser = useCallback(
    async (name) => {
      localStorage.setItem(DEV_USER_KEY, name);
      logout();
      setReady(false);
      await boot();
    },
    [boot]
  );

  const value = {
    me,
    meta,
    alerts,
    error,
    ready,
    today: meta?.today,
    reloadAlerts: loadAlerts,
    reload: boot,
    switchDevUser,
    assistantOpen,
    openAssistant: () => setAssistantOpen(true),
    closeAssistant: () => setAssistantOpen(false),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/* ── Отбор, переживающий уход с экрана ──
   Человек выбрал бизнес, завёл операцию и вернулся — отбор должен остаться тем же,
   а не сброситься на «Все бизнесы». Экраны размонтируются при каждом переходе,
   поэтому значение живёт не в них, а здесь. Перезапуск мини-аппа начинает с нуля. */
const sticky = new Map();

export function useSticky(key, initial) {
  const [value, setValue] = useState(() =>
    sticky.has(key) ? sticky.get(key) : (typeof initial === "function" ? initial() : initial)
  );
  const set = useCallback(
    (next) =>
      setValue((prev) => {
        const v = typeof next === "function" ? next(prev) : next;
        sticky.set(key, v);
        return v;
      }),
    [key]
  );
  return [value, set];
}

/* Сколько ответ считается свежим: в пределах этого времени возврат на раздел
   вообще не ходит в сеть. Дальше — показываем сохранённое и молча обновляем. */
const FRESH_MS = 30_000;

/** Хук загрузки ресурса с состояниями loading/error и ручным refetch.
    Ответы кэшируются (см. lib/cache.js): повторный вход в раздел рисуется
    сразу по сохранённым данным, заставка бывает только при первой загрузке. */
export function useFetch(path, query, deps = []) {
  const key = JSON.stringify(query || {});
  const stamp = path ? cacheKey(path, query) : null;
  const first = useRef(stamp ? peek(stamp) : null);

  const [data, setData] = useState(first.current ? first.current.data : null);
  const [loading, setLoading] = useState(!!path && !first.current);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    // path === null — запрос сейчас не нужен (данные берутся из справочников)
    if (!path) {
      setLoading(false);
      return undefined;
    }
    let alive = true;

    /* Сохранённый ответ показываем немедленно — даже устаревший.
       Пустой экран с заставкой хуже, чем данные минутной давности. */
    const hit = peek(stamp);
    if (hit) {
      setData(hit.data);
      setErr(null);
      setLoading(false);
      if (!hit.stale && Date.now() - hit.at < FRESH_MS) return undefined;
    } else {
      setLoading(true);
    }

    shared(stamp, () => api.get(path, query))
      .then((d) => {
        put(stamp, d);
        if (!alive) return;
        setData(d);
        setErr(null);
      })
      // Молчаливое обновление не должно ронять уже показанный экран в ошибку
      .catch((e) => alive && !hit && setErr(e.message))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key, tick, ...deps]);

  /* Локальные правки карточки (оптимистичное обновление) пишем и в кэш —
     иначе возврат на экран показал бы состояние до правки. */
  const update = useCallback(
    (next) =>
      setData((prev) => {
        const v = typeof next === "function" ? next(prev) : next;
        if (stamp) put(stamp, v);
        return v;
      }),
    [stamp]
  );

  const refetch = useCallback(() => {
    if (stamp) invalidate(stamp);
    setTick((t) => t + 1);
  }, [stamp]);

  return { data, loading, error: err, refetch, setData: update };
}
